import { useTranslation } from "react-i18next";
import { FiCreditCard, FiDownload } from "react-icons/fi";
import { CustomerBillingDto } from "../../../../Common/Types-DTOs/DetailedSubscription";
import {
  Card,
  CardHeader,
  CardTitle,
  PaymentMethodCard,
  PaymentIcon,
  PaymentDetails,
  PaymentBrand,
  PaymentExpiry,
  EditButton,
  BillingInfoSection,
  BillingItem,
  BillingLabel,
  BillingValue,
  MissingBillingValue,
  InvoicesList,
  InvoiceItem,
  InvoiceDate,
  InvoiceDescription,
  InvoiceAmount,
  DownloadButton,
  ViewAllLink,
} from "./Styles";

interface CustomerBillingProps {
  billing: CustomerBillingDto;
  /** Opens the Stripe customer portal, where all of it is edited. */
  onManage: () => void;
}

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: currency.toUpperCase() }).format(amount);

const formatDate = (date: Date) => date.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

/** The card the customer pays with, whatever for. */
export const PaymentMethodSection = ({ billing, onManage }: CustomerBillingProps) => {
  const { t } = useTranslation();
  const { paymentMethod } = billing;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pricing.subscriptionContent.paymentMethod.cardTitle")}</CardTitle>
      </CardHeader>
      {paymentMethod ? (
        <PaymentMethodCard>
          <PaymentIcon>
            <FiCreditCard />
          </PaymentIcon>
          <PaymentDetails>
            <PaymentBrand>
              {paymentMethod.brand
                ? paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)
                : t("pricing.subscriptionContent.paymentMethod.fallbackBrand")}{" "}
              {t("pricing.subscriptionContent.paymentMethod.endingIn", { last4: paymentMethod.last4 })}
            </PaymentBrand>
            {paymentMethod.expMonth && paymentMethod.expYear && (
              <PaymentExpiry>
                {t("pricing.subscriptionContent.paymentMethod.expires", {
                  month: paymentMethod.expMonth,
                  year: paymentMethod.expYear,
                })}
              </PaymentExpiry>
            )}
          </PaymentDetails>
          <EditButton onClick={onManage}>{t("pricing.subscriptionContent.paymentMethod.editButton")}</EditButton>
        </PaymentMethodCard>
      ) : (
        <div style={{ padding: "1rem 0", color: "#6B7280" }}>
          {t("pricing.subscriptionContent.paymentMethod.notConfigured")}
        </div>
      )}
    </Card>
  );
};

/** The customer's billing details and latest invoices, of every purchase. */
export const BillingDetailsSection = ({ billing: customerBilling, onManage }: CustomerBillingProps) => {
  const { t } = useTranslation();
  const { billing, recentInvoices } = customerBilling;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("pricing.subscriptionContent.billingInfo.cardTitle")}</CardTitle>
        </CardHeader>
        <BillingInfoSection>
          {billing.name && (
            <BillingItem>
              <BillingLabel>{t("pricing.subscriptionContent.billingInfo.legalName")}</BillingLabel>
              <BillingValue>{billing.name}</BillingValue>
            </BillingItem>
          )}
          {billing.email && (
            <BillingItem>
              <BillingLabel>{t("pricing.subscriptionContent.billingInfo.billingEmail")}</BillingLabel>
              <BillingValue>{billing.email}</BillingValue>
            </BillingItem>
          )}
          <BillingItem>
            <BillingLabel>{t("pricing.subscriptionContent.billingInfo.address")}</BillingLabel>
            {billing.address ? (
              <BillingValue>{billing.address}</BillingValue>
            ) : (
              <MissingBillingValue>
                {t("pricing.subscriptionContent.billingInfo.noAddress")} ·{" "}
                <a onClick={onManage}>{t("pricing.subscriptionContent.billingInfo.addAddress")}</a>
              </MissingBillingValue>
            )}
          </BillingItem>
          {billing.taxId && (
            <BillingItem>
              <BillingLabel>{t("pricing.subscriptionContent.billingInfo.taxId")}</BillingLabel>
              <BillingValue>{billing.taxId}</BillingValue>
            </BillingItem>
          )}
          <EditButton style={{ marginTop: "0.5rem" }} onClick={onManage}>
            {t("pricing.subscriptionContent.billingInfo.updateButton")}
          </EditButton>
        </BillingInfoSection>
      </Card>

      <Card>
        <CardHeader style={{ marginBottom: "1rem" }}>
          <CardTitle>{t("pricing.subscriptionContent.invoices.cardTitle")}</CardTitle>
          <ViewAllLink onClick={onManage}>{t("pricing.subscriptionContent.invoices.viewAllLink")}</ViewAllLink>
        </CardHeader>
        <InvoicesList>
          {recentInvoices.slice(0, 3).map((invoice) => (
            <InvoiceItem key={invoice.id}>
              <div>
                <InvoiceDate>{formatDate(invoice.date)}</InvoiceDate>
                <InvoiceDescription>
                  {invoice.description || t("pricing.subscriptionContent.invoices.fallbackDescription")}
                </InvoiceDescription>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <InvoiceAmount>{formatCurrency(invoice.amount, invoice.currency)}</InvoiceAmount>
                {invoice.url && (
                  <DownloadButton
                    onClick={() => window.open(invoice.url!, "_blank")}
                    title={t("pricing.subscriptionContent.invoices.downloadButtonTitle")}
                  >
                    <FiDownload />
                  </DownloadButton>
                )}
              </div>
            </InvoiceItem>
          ))}
          {recentInvoices.length === 0 && (
            <div style={{ padding: "2rem", textAlign: "center", color: "#6B7280", fontSize: "0.875rem" }}>
              {t("pricing.subscriptionContent.invoices.empty")}
            </div>
          )}
        </InvoicesList>
      </Card>
    </>
  );
};
